import React, {memo, useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {queries} from "shared/api/queries";
import "./sectorTable.css";
import {getApiErrorMsg, isEnglish, isKorean} from "shared/lib/fns";
import {useDataAll} from "shared/hooks/useDataAll";
import {useSearchParams} from "react-router-dom";
import {PlanSearchParamKey, TSectorItem} from "features/plan/types";
import {LeftOutlined} from "shared/assets/svgs/LeftOutlined";
import {useTableSelection} from "features/plan/hooks/useTableSelection";
import {SelectableTable} from "features/plan/components/filter/SelectableTable";
import {LoadingOutlined} from "../../../../shared/assets/svgs/LoadingOutlined";

type SortedSector = {
  code: string;
};

const KEY = "code"
const DEFAULT_PARAM = {allowed: "1"};
const CHECKBOX_COLUMN_WIDTH = "40px";
export const SectorTable = memo(() => {
  const {t} = useTranslation(["shipping"]);
  const [params, setSearchParams] = useSearchParams();
  const {listData, isLoading, error} = useDataAll<TSectorItem>({url: queries.location.sectors, params: DEFAULT_PARAM});
  
  const sortedData = useMemo(() => {
    if (!listData?.sort) {
      return [];
    }
    
    const korean: Record<string, SortedSector> = {};
    const english: Record<string, SortedSector> = {};
    const other: Record<string, SortedSector> = {};
    
    listData
    .sort()
    .forEach((item) => {
      const code = item.code.replace(/[0-9]/g, "");
      
      if (isKorean(item.code)) {
        korean[code] = {code};
        return;
      }
      
      if (isEnglish(item.code)) {
        english[code] = {code};
        return;
      }
      
      if (item.code) {
        other[code] = {code};
      }
    });
    
    const koreanList = Object.keys(korean)
                             .map((key) => ({code: key}));
    const englishList = Object.keys(english)
                              .map((key) => ({code: key}));
    const otherList = Object.keys(other)
                            .map((key) => ({code: key}));
    
    return [...koreanList, ...englishList, ...otherList];
  }, [listData]);
  
  const selectedRowKeys = useMemo(() => params.get(PlanSearchParamKey.clusterIdStartswithInCsv)
                                              ?.split(",")
                                              ?.filter(Boolean) ?? [], [params]);
  
  const handleChangeSelectedRowKeys = useCallback((keys: string[]) => {
    setSearchParams((params) => {
      if (keys.length > 0) {
        params.set(PlanSearchParamKey.clusterIdStartswithInCsv, keys.join(","));
      }
      else {
        params.delete(PlanSearchParamKey.clusterIdStartswithInCsv);
      }
      return params;
    });
  }, [setSearchParams]);
  
  const {
          selectedKeySet, isAllSelected, isPartiallySelected, handleToggleAll, toggleRow,
        } = useTableSelection({
    rows: sortedData,
    selectedRowKeys,
    getRowKey: (row) => row[KEY],
    onChangeSelectedRowKeys: handleChangeSelectedRowKeys,
  });
  
  const columns = useMemo(() => [
    {
      key: KEY, title: t("sector"), className: "cell", render: (row: SortedSector) => (<>
        <span>{row[KEY]}</span>
        <LeftOutlined
          width={10}
          height={10}
          data-state={selectedKeySet.has(row[KEY]) ? "selected" : undefined}
          className={"arrow"} />
      </>),
    }
  ], [selectedKeySet, t]);
  
  const gridTemplateColumns = `${CHECKBOX_COLUMN_WIDTH} 1fr`;
  
  return (<div className={"sector-table"}>
    <div className={"min-h-0 flex-1 overflow-auto"}>
      <SelectableTable
        rows={sortedData}
        columns={columns}
        getRowKey={(row) => row[KEY]}
        isLoading={isLoading}
        error={error}
        errorLabel={error ? getApiErrorMsg(error) : undefined}
        gridTemplateColumns={gridTemplateColumns}
        selectedKeySet={selectedKeySet}
        isAllSelected={isAllSelected}
        isPartiallySelected={isPartiallySelected}
        onToggleAll={handleToggleAll}
        onToggleRow={toggleRow}
      />
    </div>
  </div>);
});
